import React, { useEffect, useState } from 'react';
import { Radar, Loader2, MapPinOff } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { usePlatformSettings } from '../../contexts/PlatformSettingsContext';

/** Super-admin: platform-wide cap on how far away workers may see shifts. */
export default function AdminVisibilityRadiusCard() {
  const { maxVisibilityRadiusKm, requireLocationForJobs, refresh } = usePlatformSettings();
  const [radius, setRadius] = useState('');
  const [requireLoc, setRequireLoc] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setRadius(maxVisibilityRadiusKm ?? ''); }, [maxVisibilityRadiusKm]);
  useEffect(() => { setRequireLoc(!!requireLocationForJobs); }, [requireLocationForJobs]);

  const save = async (payload) => {
    setBusy(true);
    try {
      await apiCall('/api/admin/platform-settings/job-visibility-radius', { method: 'PATCH', body: JSON.stringify(payload) });
      await refresh();
      toast.success('Job visibility radius saved');
    } catch (e) {
      toast.error(e?.message || 'Save failed');
    }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="admin-visibility-radius-card">
      <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Radar className="w-5 h-5 text-sky-600" /> Job Visibility Radius
        </h3>
        <span className={`rounded-full px-3 py-1 text-xs font-bold ${maxVisibilityRadiusKm ? 'bg-sky-100 text-sky-800' : 'bg-slate-200 text-slate-700'}`} data-testid="visibility-radius-status">
          {maxVisibilityRadiusKm ? `MAX ${maxVisibilityRadiusKm} KM` : 'UNLIMITED'}
        </span>
      </div>
      <div className="p-6 space-y-4">
        <p className="text-sm text-slate-600 leading-relaxed">
          Workers only see shifts within this distance of their current location. Organisations can set a
          <b> smaller</b> radius in their Workforce Policies, never a larger one. Leave blank for no platform limit.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs font-medium text-slate-500">Maximum radius (km)</label>
            <input type="number" min="1" max="1000" value={radius} placeholder="Unlimited"
              onChange={(e) => setRadius(e.target.value)}
              className="mt-1 block w-40 px-3 py-2 border border-slate-200 rounded-lg text-sm"
              data-testid="visibility-radius-input" />
          </div>
          <button type="button" disabled={busy} onClick={() => save({ max_radius_km: radius === '' ? null : Number(radius) })}
            className="px-4 py-2 rounded-lg bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="visibility-radius-save">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save radius
          </button>
        </div>
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
          <div>
            <p className="font-semibold text-slate-900 flex items-center gap-2"><MapPinOff className="w-4 h-4 text-slate-500" /> Require location to browse shifts</p>
            <p className="text-xs text-slate-500 mt-0.5">When on, workers who have location disabled see no shifts until they enable it.</p>
          </div>
          <button type="button" role="switch" aria-checked={requireLoc} disabled={busy}
            onClick={() => { const next = !requireLoc; setRequireLoc(next); save({ require_location: next }); }}
            className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors disabled:opacity-60 ${requireLoc ? 'bg-sky-600' : 'bg-slate-300'}`}
            data-testid="require-location-switch">
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${requireLoc ? 'translate-x-8' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>
    </div>
  );
}
